import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import './adminNotice.css';
import API_BASE_URL from '../../../config/api';

const ITEMS_PER_PAGE = 10;

const AdminNoticeList = () => {
    const [notices, setNotices] =
        useState([]);
    const [selectedType, setSelectedType] =
        useState('전체');
    const [searchKeyword, setSearchKeyword] =
        useState('');
    const [currentPage, setCurrentPage] =
        useState(1);
    const [selectedNotice, setSelectedNotice] =
        useState(null);
    const [isLoading, setIsLoading] =
        useState(true);
    const [isDetailLoading, setIsDetailLoading] =
        useState(false);
    const [error, setError] =
        useState('');
    const [detailError, setDetailError] =
        useState('');

    /*
     * ================================================
     * NOTICE LIST
     * ================================================
     */

    useEffect(() => {
        const fetchNotices = async () => {
            try {
                setIsLoading(true);
                setError('');
                const response = await fetch(
                    `${API_BASE_URL}/api/notices/admin`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );
                const result =
                    await response.json();
                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '공지사항을 불러오지 못했습니다.'
                    );
                }
                setNotices(
                    result.notices || []
                );
            } catch (error) {
                console.error(
                    '공지사항 조회 오류:',
                    error
                );
                setError(
                    error.message
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchNotices();
    }, []);

    /*
     * ================================================
     * BODY SCROLL
     * ================================================
     */

    useEffect(() => {
        if (
            selectedNotice ||
            isDetailLoading ||
            detailError
        ) {
            document.body.style.overflow =
                'hidden';
        } else {
            document.body.style.overflow =
                '';
        }
        return () => {
            document.body.style.overflow =
                '';
        };
    }, [
        selectedNotice,
        isDetailLoading,
        detailError
    ]);

    /*
     * ================================================
     * NOTICE TYPES
     * ================================================
     */

    const noticeTypes =
        useMemo(() => {
            const types =
                notices
                    .map(
                        (notice) =>
                            notice.notice_type
                    )
                    .filter(Boolean);
            return [
                '전체',
                ...Array.from(
                    new Set(types)
                )
            ];
        }, [notices]);

    /*
     * ================================================
     * FILTER
     * ================================================
     */

    const filteredNotices =
        useMemo(() => {
            const keyword =
                searchKeyword
                    .trim()
                    .toLowerCase();
            return notices.filter(
                (notice) => {
                    const matchesType =
                        selectedType === '전체' ||
                        notice.notice_type ===
                        selectedType;
                    if (!matchesType) {
                        return false;
                    }
                    if (!keyword) {
                        return true;
                    }
                    const searchableText = [
                        notice.title,
                        notice.notice_type,
                        notice.writer,
                        notice.content,
                        notice.created_at
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return searchableText.includes(
                        keyword
                    );
                }
            );
        }, [
            notices,
            selectedType,
            searchKeyword
        ]);
    const handleTypeChange = (type) => {
        setSelectedType(type);
        setCurrentPage(1);
    };
    const handleSearchChange = (event) => {
        setSearchKeyword(
            event.target.value
        );
        setCurrentPage(1);
    };
    const handleClearSearch = () => {
        setSearchKeyword('');
        setCurrentPage(1);
    };
    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredNotices.length /
            ITEMS_PER_PAGE
        )
    );
    const paginatedNotices =
        useMemo(() => {
            const startIndex =
                (currentPage - 1) *
                ITEMS_PER_PAGE;
            const endIndex =
                startIndex +
                ITEMS_PER_PAGE;
            return filteredNotices.slice(
                startIndex,
                endIndex
            );
        }, [
            filteredNotices,
            currentPage
        ]);
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(
                totalPages
            );
        }
    }, [
        currentPage,
        totalPages
    ]);
    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from(
                {
                    length: totalPages
                },
                (_, index) =>
                    index + 1
            );
        }
        let startPage = Math.max(
            1,
            currentPage - 2
        );
        const endPage = Math.min(
            totalPages,
            startPage + 4
        );
        if (endPage - startPage < 4) {
            startPage = Math.max(
                1,
                endPage - 4
            );
        }
        return Array.from(
            {
                length:
                    endPage - startPage + 1
            },
            (_, index) =>
                startPage + index
        );
    }, [
        currentPage,
        totalPages
    ]);

    /*
     * ================================================
     * DATE
     * ================================================
     */

    const formatDate = (date) => {
        if (!date) {
            return '-';
        }
        return new Intl.DateTimeFormat(
            'ko-KR',
            {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }
        ).format(
            new Date(date)
        );
    };

    /*
     * ================================================
     * DETAIL
     * ================================================
     */

    const handleView = async (
        noticeId
    ) => {
        try {
            setIsDetailLoading(true);
            setDetailError('');
            setSelectedNotice(null);
            const response = await fetch(
                `${API_BASE_URL}/api/notices/admin/${noticeId}`,
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );
            const result =
                await response.json();
            if (!response.ok) {
                throw new Error(
                    result.message ||
                    '공지사항을 불러오지 못했습니다.'
                );
            }
            setSelectedNotice(
                result.notice
            );
        } catch (error) {
            console.error(
                '공지사항 상세 조회 오류:',
                error
            );
            setDetailError(
                error.message
            );
        } finally {
            setIsDetailLoading(false);
        }
    };
    const handleCloseDetail = () => {
        setSelectedNotice(null);
        setDetailError('');
    };

    /*
     * ================================================
     * DELETE
     * ================================================
     */

    const handleDelete = async (
        noticeId,
        title
    ) => {
        const confirmed =
            window.confirm(
                `"${title}" 공지사항을 삭제하시겠습니까?\n삭제된 내용은 복구할 수 없습니다.`
            );
        if (!confirmed) {
            return;
        }
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/notices/admin/${noticeId}`,
                {
                    method: 'DELETE',
                    credentials: 'include'
                }
            );
            const result =
                await response.json();
            if (!response.ok) {
                throw new Error(
                    result.message ||
                    '공지사항 삭제에 실패했습니다.'
                );
            }
            setNotices((prev) =>
                prev.filter(
                    (notice) =>
                        notice.notice_id !==
                        noticeId
                )
            );
            if (
                selectedNotice?.notice_id ===
                noticeId
            ) {
                handleCloseDetail();
            }
            alert(
                '공지사항이 삭제되었습니다.'
            );
        } catch (error) {
            console.error(
                '공지사항 삭제 오류:',
                error
            );
            alert(
                error.message
            );
        }
    };
    return (
        <div className="admin-notice">
            {/* HEADER */}
            <div className="admin-notice-header">
                <div>
                    <p>
                        NOTICE
                    </p>
                    <h1>
                        공지사항 관리
                    </h1>
                    <span>
                        홈페이지에 노출되는 공지사항을 관리합니다.
                    </span>
                </div>
                <Link
                    to="/admin/notices/new"
                    className="admin-notice-add"
                >
                    <i className="mdi mdi-plus"></i>
                    공지사항 등록
                </Link>
            </div>
            {/* CONTENT */}
            <div className="admin-notice-content">
                {/* TYPE + SEARCH */}
                <div className="admin-notice-toolbar">
                    <div className="admin-notice-tabs">
                        {noticeTypes.map(
                            (type) => (
                                <button
                                    type="button"
                                    key={type}
                                    className={
                                        selectedType === type
                                            ? 'active'
                                            : ''
                                    }
                                    onClick={() =>
                                        handleTypeChange(
                                            type
                                        )
                                    }
                                >
                                    {type}
                                </button>
                            )
                        )}
                    </div>
                    <div className="admin-notice-search-area">
                        <span className="admin-notice-result-count">
                            {filteredNotices.length}건
                        </span>
                        <div className="admin-notice-search">
                            <i className="mdi mdi-magnify"></i>
                            <input
                                type="text"
                                value={searchKeyword}
                                onChange={handleSearchChange}
                                placeholder="제목, 작성자, 내용 검색"
                                aria-label="공지사항 검색"
                            />
                            {searchKeyword && (
                                <button
                                    type="button"
                                    className="admin-notice-search-clear"
                                    onClick={handleClearSearch}
                                    aria-label="검색어 지우기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {isLoading ? (
                    <div className="admin-notice-empty">
                        <i className="mdi mdi-loading mdi-spin"></i>
                        <strong>
                            공지사항을 불러오는 중입니다.
                        </strong>
                    </div>
                ) : error ? (
                    <div className="admin-notice-empty">
                        <i className="mdi mdi-alert-circle-outline"></i>
                        <strong>
                            공지사항을 불러오지 못했습니다.
                        </strong>
                        <p>
                            {error}
                        </p>
                    </div>
                ) : filteredNotices.length === 0 ? (
                    <div className="admin-notice-empty">
                        <i className="mdi mdi-bullhorn-outline"></i>
                        <strong>
                            등록된 공지사항이 없습니다.
                        </strong>
                        <p>
                            새로운 공지사항을 등록해주세요.
                        </p>
                    </div>
                ) : (
                    <div className="admin-notice-results">
                        <div className="admin-notice-table-wrap">
                        <table className="admin-notice-table">
                            <thead>
                                <tr>
                                    <th>
                                        번호
                                    </th>
                                    <th>
                                        구분
                                    </th>
                                    <th>
                                        제목
                                    </th>
                                    <th>
                                        작성자
                                    </th>
                                    <th>
                                        조회수
                                    </th>
                                    <th>
                                        등록일
                                    </th>
                                    <th>
                                        관리
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedNotices.map(
                                    (notice) => (
                                        <tr
                                            key={
                                                notice.notice_id
                                            }
                                        >
                                            <td>
                                                {
                                                    notice.notice_id
                                                }
                                            </td>
                                            <td>
                                                <span className="admin-notice-type">
                                                    {
                                                        notice.notice_type
                                                    }
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="admin-notice-title"
                                                    onClick={() =>
                                                        handleView(
                                                            notice.notice_id
                                                        )
                                                    }
                                                >
                                                    {
                                                        notice.title
                                                    }
                                                </button>
                                            </td>
                                            <td>
                                                {
                                                    notice.writer
                                                }
                                            </td>
                                            <td>
                                                {
                                                    notice.view_count
                                                }
                                            </td>
                                            <td>
                                                {formatDate(
                                                    notice.created_at
                                                )}
                                            </td>
                                            <td>
                                                <div className="admin-notice-actions">
                                                    <button
                                                        type="button"
                                                        className="admin-notice-view"
                                                        onClick={() =>
                                                            handleView(
                                                                notice.notice_id
                                                            )
                                                        }
                                                    >
                                                        보기
                                                    </button>
                                                    <Link
                                                        to={`/admin/notices/${notice.notice_id}/edit`}
                                                        className="admin-notice-edit"
                                                    >
                                                        수정
                                                    </Link>
                                                    <button
                                                        type="button"
                                                        className="admin-notice-delete"
                                                        onClick={() =>
                                                            handleDelete(
                                                                notice.notice_id,
                                                                notice.title
                                                            )
                                                        }
                                                    >
                                                        삭제
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                        {totalPages > 1 && (
                            <div className="admin-notice-pagination">
                                <button
                                    type="button"
                                    className="admin-notice-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.max(
                                                    prev - 1,
                                                    1
                                                )
                                        )
                                    }
                                    disabled={currentPage === 1}
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left"></i>
                                </button>
                                <div className="admin-notice-pagination-pages">
                                    {pageNumbers.map(
                                        (page) => (
                                            <button
                                                key={page}
                                                type="button"
                                                className={
                                                    currentPage === page
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="admin-notice-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.min(
                                                    prev + 1,
                                                    totalPages
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* LOADING MODAL */}
            {isDetailLoading && (
                <div className="admin-notice-modal-overlay">
                    <div className="admin-notice-modal-loading">
                        <i className="mdi mdi-loading mdi-spin"></i>
                        <strong>
                            공지사항을 불러오는 중입니다.
                        </strong>
                    </div>
                </div>
            )}
            {/* ERROR MODAL */}
            {!isDetailLoading &&
                detailError && (
                    <div
                        className="admin-notice-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-notice-modal-loading"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <i className="mdi mdi-alert-circle-outline"></i>
                            <strong>
                                공지사항을 불러오지 못했습니다.
                            </strong>
                            <p>
                                {detailError}
                            </p>
                            <button
                                type="button"
                                onClick={handleCloseDetail}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                )}
            {/* DETAIL MODAL */}
            {!isDetailLoading &&
                selectedNotice && (
                    <div
                        className="admin-notice-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-notice-modal"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <div className="admin-notice-modal-header">
                                <div>
                                    <p>
                                        NOTICE DETAIL
                                    </p>
                                    <h2>
                                        {
                                            selectedNotice.title
                                        }
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    className="admin-notice-modal-close"
                                    onClick={
                                        handleCloseDetail
                                    }
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            </div>
                            <div className="admin-notice-modal-body">
                                <div className="admin-notice-detail-info">
                                    <DetailRow
                                        label="구분"
                                        value={
                                            selectedNotice.notice_type
                                        }
                                    />
                                    <DetailRow
                                        label="작성자"
                                        value={
                                            selectedNotice.writer
                                        }
                                    />
                                    <DetailRow
                                        label="등록일"
                                        value={
                                            formatDate(
                                                selectedNotice.created_at
                                            )
                                        }
                                    />
                                    <DetailRow
                                        label="조회수"
                                        value={
                                            selectedNotice.view_count
                                        }
                                    />
                                </div>
                                <div className="admin-notice-detail-content">
                                    <strong>
                                        내용
                                    </strong>
                                    <div>
                                        {
                                            selectedNotice.content
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="admin-notice-modal-footer">
                                <Link
                                    to={`/support/notice/${selectedNotice.notice_id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="admin-notice-public"
                                >
                                    실제 페이지 보기
                                    <i className="mdi mdi-open-in-new"></i>
                                </Link>
                                <Link
                                    to={`/admin/notices/${selectedNotice.notice_id}/edit`}
                                    className="admin-notice-modal-edit"
                                >
                                    수정하기
                                </Link>
                                <button
                                    type="button"
                                    className="admin-notice-modal-cancel"
                                    onClick={
                                        handleCloseDetail
                                    }
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

const DetailRow = ({
    label,
    value
}) => {
    return (
        <div className="admin-notice-detail-row">
            <strong>
                {label}
            </strong>
            <span>
                {value !== null &&
                value !== undefined &&
                value !== ''
                    ? value
                    : '-'
                }
            </span>
        </div>
    );
};
export default AdminNoticeList;
